/** Reusable, pure formatting helpers. */

/**
 * Today as a local 'YYYY-MM-DD' string — for DateField `min` floors. NOT
 * `new Date().toISOString().split('T')[0]`, which converts to UTC: for a
 * timezone ahead of UTC (e.g. Asia/Kolkata, UTC+5:30) that rolls back to
 * yesterday's date for the first ~5.5 hours of every local day, silently
 * letting a past date through a "no past dates" floor.
 */
export const todayISO = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** Indian-locale currency (e.g. 500000 -> "₹5,00,000"). */
export const formatCurrencyINR = (value) => {
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(n);
};

/** Indian short form: 5000000 -> "5 Cr", 250000 -> "2.5 Lakh", 5000 -> "5k". */
export const toIndianShort = (value) => {
  const n = Math.abs(Number(value) || 0);
  if (!n) return '0';
  const fmt = (v) => {
    const r = Math.round(v * 100) / 100;
    return Number.isInteger(r) ? String(r) : String(r).replace(/\.?0+$/, '');
  };
  if (n >= 1e7) return `${fmt(n / 1e7)} Cr`;
  if (n >= 1e5) return `${fmt(n / 1e5)} Lakh`;
  if (n >= 1e3) return `${fmt(n / 1e3)}k`;
  return n.toLocaleString('en-IN');
};

// --- dates ---------------------------------------------------------------
// "13 August, 2026" is the app-wide date format. Everything user-facing goes
// through these helpers rather than toLocaleDateString, so the rendering never
// drifts with the viewer's locale (en-US would give "August 13, 2026").

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const PLAIN_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Coerce anything date-ish — Date, 'YYYY-MM-DD', ISO datetime, epoch ms — into
 * calendar parts, or null when it isn't a date. A bare 'YYYY-MM-DD' is read
 * literally so a wire date never shifts a day across timezones.
 */
const toParts = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'string') {
    const plain = PLAIN_DATE.exec(value.trim());
    if (plain) {
      return { y: +plain[1], m: +plain[2], d: +plain[3], hh: 0, mm: 0, timed: false };
    }
  }
  const dt = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dt.getTime())) return null;
  return {
    y: dt.getFullYear(),
    m: dt.getMonth() + 1,
    d: dt.getDate(),
    hh: dt.getHours(),
    mm: dt.getMinutes(),
    timed: true,
  };
};

/** "13 August, 2026" — the app-wide date format. '' when not a date. */
export const formatLongDate = (value) => {
  const p = toParts(value);
  return p ? `${p.d} ${MONTHS[p.m - 1]}, ${p.y}` : '';
};

/** "Thursday, 13 August, 2026" — long date with its weekday (page headers). */
export const formatLongDateWithWeekday = (value) => {
  const p = toParts(value);
  if (!p) return '';
  const weekday = new Date(p.y, p.m - 1, p.d).toLocaleDateString('en-GB', { weekday: 'long' });
  return `${weekday}, ${p.d} ${MONTHS[p.m - 1]}, ${p.y}`;
};

const TIME_ONLY = /^(\d{1,2}):(\d{2})(?::\d{2})?$/;

const to12Hour = (hh, mm) =>
  `${hh % 12 || 12}:${String(mm).padStart(2, '0')} ${hh < 12 ? 'AM' : 'PM'}`;

/**
 * "3:05 PM" — 12-hour clock, matching the time pickers. Accepts a
 * timestamp or a bare wall-clock string ('15:05' / '15:05:00'), which is what
 * the session endpoints return for start_time / end_time.
 */
export const formatTime = (value) => {
  if (typeof value === 'string') {
    const t = TIME_ONLY.exec(value.trim());
    if (t) return to12Hour(+t[1], +t[2]);
  }
  const p = toParts(value);
  return p ? to12Hour(p.hh, p.mm) : '';
};

/** "13 August, 2026, 3:05 PM" — long date plus time. */
export const formatLongDateTime = (value) => {
  const p = toParts(value);
  if (!p) return '';
  const date = `${p.d} ${MONTHS[p.m - 1]}, ${p.y}`;
  return p.timed ? `${date}, ${to12Hour(p.hh, p.mm)}` : date;
};
